// AppShell — shell de la app móvil cliente (/app/*).
//
// Guard: SOLO cliente en móvil (<860px) o PWA standalone. Admin y cliente
// desktop van al dashboard actual — esa UI queda intacta.
// Flujo: Splash (mín 1.2s, mientras carga la sesión) → Login (sin sesión)
// o pantalla activa (con sesión) + TabBar.
//
// Handoff v3 (FASE 1): pantallas pre-sesión nuevas (08 Registro, 02.1/02.2
// Recuperar) se resuelven acá porque sin `user` el shell muestra el login;
// 07 Onboarding se dispara una sola vez tras crear cuenta (flag local);
// las subpantallas push (detalle, cuenta, telegram, notificaciones, agenda)
// ocultan la tab bar.
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isPausedFor, SERVICE_PAUSED } from '../config/servicePaused';
import ServicePaused from '../pages/ServicePaused';
import { useAuth } from '../auth/AuthContext';
import Splash from './v3/screens/Splash';
import AppLogin from './v3/screens/Login';
import { useLiveGames } from '../hooks/useLiveGames';
import { useFollowAlerts } from './v3/live';
import TabBar from './components/TabBar';
import Registro from './v3/screens/Registro';
import { RecuperarPaso1, RecuperarEnviado } from './v3/screens/Recuperar';
import { OB_PENDING_KEY } from './v3/screens/Onboarding';
import { ToastProvider } from './v3/ui';
import './tokens.css';
import './v3.css';
import './v3-f2.css';

const isMobileViewport = () => window.innerWidth < 860;

// Update prompt PWA: el SW hace skipWaiting automático, así que cuando un
// SW nuevo toma control (controllerchange) la página que quedó abierta
// sigue corriendo el bundle viejo. Este hook lo detecta y ofrece recargar.
// Además fuerza un chequeo de SW al volver la app a foreground (caso PWA
// que vive días en memoria sin navegar).
function useSwUpdatePrompt() {
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    let hadController = !!navigator.serviceWorker.controller;
    const onControllerChange = () => {
      if (hadController) setUpdateReady(true);
      hadController = true;
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const checkUpdate = () => {
      navigator.serviceWorker.getRegistration()
        .then((reg) => {
          if (!reg) return;
          if (reg.waiting) {
            // SW nuevo instalado pero esperando (borde raro): activarlo ya
            reg.waiting.postMessage('SKIP_WAITING');
            setUpdateReady(true);
          }
          reg.update().catch(() => {});
        })
        .catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkUpdate();
    };
    checkUpdate();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return updateReady;
}

function UpdateBanner() {
  return (
    <div style={{
      position: 'fixed', left: 12, right: 12,
      bottom: 'calc(74px + env(safe-area-inset-bottom))', zIndex: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 10, padding: '12px 14px', borderRadius: 14,
      background: 'var(--card, #16181d)', border: '1px solid var(--line, #2a2d34)',
      boxShadow: '0 8px 24px rgba(0,0,0,.45)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--ink, #fff)' }}>Nueva versión disponible</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          border: 'none', borderRadius: 10, padding: '8px 14px',
          background: 'var(--brand, #ff5c1a)', color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}
      >
        Actualizar
      </button>
    </div>
  );
}

// 10 · "Seguir este partido": aviso local cuando un partido seguido sale
// de /live_games (cerró). Vive en el shell para que funcione en cualquier
// pantalla mientras la app esté abierta.
function FollowWatcher() {
  const { byId } = useLiveGames();
  useFollowAlerts(byId);
  return null;
}

// 01 · error de carga: 8 s sin sesión resuelta → tarjeta de reintento;
// reintento automático cada 5 s (máx. 3, solo con red) y manual.
function useSplashError(loading) {
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!loading) { setErr(false); return undefined; }
    const t = setTimeout(() => setErr(true), 8000);
    return () => clearTimeout(t);
  }, [loading]);
  useEffect(() => {
    if (!err) return undefined;
    let n = 0;
    const t = setInterval(() => { n += 1; if (n <= 3 && navigator.onLine) window.location.reload(); }, 5000);
    return () => clearInterval(t);
  }, [err]);
  return err;
}

// Subpantallas push (con .nav propio): sin tab bar.
const PUSH_RE = /^\/app\/(partido\/|cuenta|telegram|notificaciones|agenda|bienvenida|registro|recuperar)/;

// REGLA DEFINITIVA (Matías): el dashboard original es el default para
// TODOS los roles en móvil y desktop. /app es OPCIONAL — se llega solo
// por link explícito o por la PWA instalada (start_url /app). Por eso
// acá NO hay ningún redirect: quien navegó a /app lo eligió.
export default function AppShell() {
  const { user, loading, userDataReady, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const [minSplash, setMinSplash] = useState(true);
  const updateReady = useSwUpdatePrompt();
  const cargando = loading || (user && !userDataReady);
  const splashErr = useSplashError(!!cargando);

  useEffect(() => {
    const t = setTimeout(() => setMinSplash(false), 1200);
    return () => clearTimeout(t);
  }, []);

  // Igual que ProtectedRoute: con sesión pero sin el doc del usuario aún,
  // hasActiveAccess daría false y las pantallas mostrarían todo bloqueado
  // a un cliente con acceso vigente. Se mantiene el splash hasta tener datos.
  if (cargando || minSplash) {
    return <div className="app-root"><Splash error={splashErr && !!cargando} onRetry={() => window.location.reload()} /></div>;
  }

  // Pausa del servicio (21-sep-2026). /app no pasa por ProtectedRoute, así
  // que el corte va acá. Después del splash, para no decidir con el doc del
  // usuario a medio cargar, y antes del onboarding y de cualquier pantalla
  // con datos. El admin sigue entrando a todo.
  if (user && isPausedFor(isAdmin)) {
    return <ServicePaused />;
  }

  // Onboarding (07): solo una vez, tras crear cuenta desde /app/registro.
  let obPending = false;
  try { obPending = !!localStorage.getItem(OB_PENDING_KEY); } catch (_e) { /* noop */ }
  if (user && obPending && !pathname.startsWith('/app/bienvenida')) {
    return <Navigate to="/app/bienvenida" replace />;
  }

  const sinTabs = PUSH_RE.test(pathname);

  // Pantallas pre-sesión del handoff v3.
  // Con el servicio en pausa el registro queda cerrado: crear una cuenta
  // nueva no serviría de nada y además llevaría al checkout, que también
  // está cerrado. Recuperar contraseña sí sigue, porque quien ya tiene
  // cuenta necesita poder entrar a verla.
  let preAuth = null;
  if (!user) {
    if (pathname === '/app/registro') {
      preAuth = SERVICE_PAUSED ? <ServicePaused conSalir={false} /> : <Registro />;
    } else if (pathname === '/app/recuperar') preAuth = <RecuperarPaso1 />;
    else if (pathname === '/app/recuperar/enviado') preAuth = <RecuperarEnviado />;
  }

  return (
    <ToastProvider>
      <div className="app-root">
        {!user ? (
          preAuth || <AppLogin />
        ) : (
          <>
            <FollowWatcher />
            <Outlet />
            {!sinTabs && <TabBar />}
          </>
        )}
        {updateReady && <UpdateBanner />}
      </div>
    </ToastProvider>
  );
}
